import { useEffect, useRef, useState } from "react";
import PageShell from "@/components/PageShell";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import YadidAI from "@/components/YadidAI";
import TimesheetsPanel from "@/components/TimesheetsPanel";
import DocumentsPanel from "@/components/DocumentsPanel";
import DiagnosticMemoDialog from "@/components/DiagnosticMemoDialog";
import WeeklyBriefDialog from "@/components/WeeklyBriefDialog";
import ConveningPlaybook from "@/components/ConveningPlaybook";
import RelationshipTimeline from "@/components/RelationshipTimeline";
import CloseoutMemoDialog from "@/components/CloseoutMemoDialog";
import NoteViewerDialog from "@/components/NoteViewerDialog";
import MilestoneViewerDialog from "@/components/MilestoneViewerDialog";
import DeliverableViewerDialog from "@/components/DeliverableViewerDialog";
import TaskQuickViewDialog from "@/components/TaskQuickViewDialog";
import InvoiceQuickViewDialog from "@/components/InvoiceQuickViewDialog";
import MethodPhaseDrawer from "@/components/MethodPhaseDrawer";
import MethodEmphasisDialog from "@/components/MethodEmphasisDialog";
import EngagementHeader from "@/components/engagement/EngagementHeader";
import EngagementStatusGrid from "@/components/engagement/EngagementStatusGrid";
import EngagementOverviewTab from "@/components/engagement/EngagementOverviewTab";
import EngagementMilestonesTab from "@/components/engagement/EngagementMilestonesTab";
import EngagementDeliverablesTab from "@/components/engagement/EngagementDeliverablesTab";
import EngagementTasksTab from "@/components/engagement/EngagementTasksTab";
import EngagementInvoicesTab from "@/components/engagement/EngagementInvoicesTab";
import EngagementNotesTab from "@/components/engagement/EngagementNotesTab";
import EngagementEmbraceTab from "@/components/engagement/EngagementEmbraceTab";
import EngagementDecisions from "@/components/engagement/EngagementDecisions";
import ActivitiesTab from "@/components/ActivitiesTab";
import { useAuth } from "@/lib/auth";

/*
  Supporting-tab trigger classes — extracted so every tab shares one source
  of truth for spacing and state colors.
    • Inactive: `text-muted-foreground` (Stone deep on Ivory, Stone bright
      on Navy) so the row reads as a menu, not as a bank of primary buttons.
    • Hover:    `hover:text-foreground hover:bg-secondary/40` for a subtle,
      theme-aware lift that also gives keyboard focus something to land on.
    • Active:   `text-primary` + a 2px Navy underline. No fill — keeps the
      row visually restrained even at full width.
    • Focus:    `focus-visible:outline-none focus-visible:bg-secondary/40`
      matches hover so keyboard users see the same affordance.
*/
const TAB_CLS =
  "rounded-none bg-transparent text-xs uppercase font-mono tracking-widest px-4 py-3 " +
  "text-muted-foreground hover:text-foreground hover:bg-secondary/40 " +
  "focus-visible:outline-none focus-visible:bg-secondary/40 " +
  "data-[state=active]:border-b-2 data-[state=active]:border-primary " +
  "data-[state=active]:text-primary data-[state=active]:bg-transparent " +
  "transition-colors";

/**
 * Engagement detail — parent orchestrator.
 *
 * Owns data loading and every side-effect (save/toggle/cycle handlers, dialog
 * state). Renders extracted section components under a Tabs shell. This file
 * used to be ~588 lines; the sub-components live in `components/engagement/*`.
 *
 * Refactor invariants:
 *   • No visible behavior changes: same wording, layout, order, testids.
 *   • Same data-loading pattern: one `load()` on mount + on-save partial refresh.
 *   • Same dialog wiring: NoteViewer / MilestoneViewer / DeliverableViewer /
 *     TaskQuickView / InvoiceQuickView / MethodPhaseDrawer / MethodEmphasisDialog.
 */
export default function EngagementDetail() {
  const { id } = useParams();
  // Optional `?tab=` query lets callers (the Client workspace, morning brief,
  // etc.) deep-link into a specific tab (`embrace` alias → the `roadmap`
  // value the TabsRoot uses, invoices, notes, timeline, …). We snapshot the
  // value once on mount so the TabsRoot stays uncontrolled and users can
  // switch tabs freely without their URL rewriting under them.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (() => {
    const raw = (searchParams.get("tab") || "").toLowerCase();
    // Iter 85.65 · Default landing tab is Embrace ("roadmap" internally).
    // Any explicit deep-link is preserved; only the empty case changes.
    if (!raw) return "roadmap";
    if (raw === "embrace") return "roadmap";
    return raw;
  })();
  const [e, setE] = useState(null);
  const [client, setClient] = useState(null);
  const [tasks, setTasks] = useState([]);
  // Iter 85.63 · Users + activities needed to power the shared
  // TaskEditDialog with correct pickers on the Engagement Tasks tab.
  const [users, setUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [notes, setNotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [aiOpen, setAiOpen] = useState(false);
  const [msOpen, setMsOpen] = useState(false);
  const [dvOpen, setDvOpen] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);
  const [weeklyOpen, setWeeklyOpen] = useState(false);
  const [closeoutOpen, setCloseoutOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [selectedDeliverable, setSelectedDeliverable] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [roadmap, setRoadmap] = useState(null);
  const [roadmapPhase, setRoadmapPhase] = useState(null);
  const [roadmapDrawerOpen, setRoadmapDrawerOpen] = useState(false);
  const [emphasisDialogOpen, setEmphasisDialogOpen] = useState(false);
  const { user: authUser } = useAuth();
  // Iter 85.74 · The Change Emphasis control now renders unconditionally
  // — the backend still enforces MP/firm-owner authority when a change
  // is actually recorded, so a non-privileged viewer can OPEN the dialog
  // but their save will be rejected with a clear message. This ensures
  // the affordance is discoverable throughout the engagement lifecycle
  // and matches user expectations that "the button is always there,
  // permission surfaces on click".
  const canChangeEmphasis = true;
  const isRoleManaging = ["partner", "principal", "managing_principal", "managing principal"].includes((authUser?.role || "").toLowerCase().replace("_", " "));
  const [ms, setMs] = useState({ title: "", due_date: "", status: "pending" });
  const [dv, setDv] = useState({ title: "", status: "in_progress", owner: "", owner_id: "", due_date: "" });
  const tabsRef = useRef(null);
  // ── Deep-link · `?milestone=<index>` ──
  // Signals-from-the-field Milestones rows navigate to
  // /engagements/{id}?tab=milestones&milestone=<idx>. When we land with
  // that param AND the engagement has loaded, open the
  // MilestoneViewerDialog for the exact item. Guarded so switching tabs
  // later doesn't fight the URL.
  const milestoneParamConsumedRef = useRef(false);
  useEffect(() => {
    if (milestoneParamConsumedRef.current) return;
    if (!e) return;
    const raw = searchParams.get("milestone");
    if (raw === null || raw === "") return;
    const idx = parseInt(raw, 10);
    if (Number.isNaN(idx)) return;
    const list = e.milestones || [];
    if (idx < 0 || idx >= list.length) return;
    milestoneParamConsumedRef.current = true;
    setSelectedMilestone({ item: list[idx], index: idx });
  }, [e, searchParams]);
  // Iter 69 · Which action-item inside `selectedNote` should the viewer
  // scroll to and highlight when we deep-link a specific commitment.
  // `null` = no highlight (regular note open).
  const [focusCommitmentIdx, setFocusCommitmentIdx] = useState(null);
  // ── Deep-link handlers · `?openTask=<id>` · `?openNote=<id>` · `?openCommitment=<idx>` (Iter 68 + 69) ──
  // Each URL land opens the exact source record. After consuming the params
  // we STRIP them from the URL so subsequent re-renders don't reopen the
  // dialog after the partner closes it, and so a fresh click from the brief
  // to a different record reliably re-fires (component doesn't remount when
  // the engagement id is unchanged).
  useEffect(() => {
    const tid = searchParams.get("openTask");
    const nid = searchParams.get("openNote");
    const cidx = searchParams.get("openCommitment");
    if (!tid && !nid) return;
    let consumed = false;
    if (tid && tasks && tasks.length) {
      const t = tasks.find((x) => x.id === tid);
      if (t) { setSelectedTask(t); consumed = true; }
      else { consumed = true; /* unknown id — silently drop */ }
    }
    if (nid && notes && notes.length) {
      const n = notes.find((x) => x.id === nid);
      if (n) {
        setSelectedNote(n);
        setFocusCommitmentIdx(Number.isFinite(parseInt(cidx, 10)) ? parseInt(cidx, 10) : null);
        consumed = true;
      } else { consumed = true; }
    }
    if (consumed) {
      const next = new URLSearchParams(searchParams);
      next.delete("openTask");
      next.delete("openNote");
      next.delete("openCommitment");
      setSearchParams(next, { replace: true });
    }
  }, [tasks, notes, searchParams, setSearchParams]);
  // Whenever the partner switches tabs, snap the anchor to the top of the
  // viewport so the panel content is immediately visible.
  const handleTabChange = () => {
    requestAnimationFrame(() => {
      tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const load = () => {
    api.get(`/engagements/${id}`).then((r) => {
      setE(r.data);
      if (r.data?.client_id) api.get(`/clients/${r.data.client_id}`).then((c) => setClient(c.data)).catch(() => {});
    });
    api.get(`/tasks?engagement_id=${id}`).then((r) => setTasks(r.data));
    api.get(`/notes?engagement_id=${id}`).then((r) => setNotes(r.data));
    api.get(`/users`).then((r) => setUsers(r.data)).catch(() => setUsers([]));
    api.get(`/notes?engagement_id=${id}&note_type=activity_briefing`).then((r) => setActivities(r.data)).catch(() => setActivities([]));
    api.get(`/invoices?engagement_id=${id}`).then((r) => setInvoices(r.data)).catch(() => setInvoices([]));
    api.get(`/engagements/${id}/roadmap`).then((r) => setRoadmap(r.data)).catch(() => setRoadmap(null));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  if (!e) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const save = async (patch) => {
    const { data } = await api.put(`/engagements/${id}`, patch);
    setE(data);
  };

  const addMilestone = async () => {
    if (!ms.title) return;
    await save({ milestones: [...(e.milestones || []), ms] });
    setMs({ title: "", due_date: "", status: "pending" });
    setMsOpen(false);
    toast.success("Milestone added");
  };
  const toggleMilestone = async (i) => {
    const list = [...(e.milestones || [])];
    list[i] = { ...list[i], status: list[i].status === "done" ? "pending" : "done" };
    await save({ milestones: list });
    setSelectedMilestone((prev) => (prev && prev.index === i ? { item: list[i], index: i } : prev));
  };
  const deleteMilestone = async (i) => {
    if (!confirm("Delete this milestone?")) return;
    const list = (e.milestones || []).filter((_, idx) => idx !== i);
    await save({ milestones: list });
    setSelectedMilestone(null);
    toast.success("Milestone removed");
  };
  const deleteDeliverable = async (i) => {
    if (!confirm("Delete this deliverable?")) return;
    const list = (e.deliverables || []).filter((_, idx) => idx !== i);
    await save({ deliverables: list });
    setSelectedDeliverable(null);
    toast.success("Deliverable removed");
  };
  // Iter 85.62 · Edit a deliverable's title/owner in place. Preserves
  // every other field on the row (status, source_briefing_id,
  // source_note_id, client_id, created_at, created_by) so provenance
  // for AI-generated deliverables survives an advisor edit.
  const editDeliverable = async (i, patch) => {
    const list = [...(e.deliverables || [])];
    if (!list[i]) return;
    list[i] = { ...list[i], ...patch, updated_at: new Date().toISOString() };
    await save({ deliverables: list });
  };
  const addDeliverable = async () => {
    if (!dv.title) return;
    // Iter 85.65 · Include owner_id + due_date + delivered_at (null) so the
    // canonical row has every downstream field the memo + organized-note
    // renderers expect.
    const row = {
      title: dv.title,
      status: dv.status || "in_progress",
      owner: dv.owner || "",
      owner_id: dv.owner_id || null,
      due_date: dv.due_date || null,
      created_at: new Date().toISOString(),
    };
    await save({ deliverables: [...(e.deliverables || []), row] });
    setDv({ title: "", status: "in_progress", owner: "", owner_id: "", due_date: "" });
    setDvOpen(false);
    toast.success("Deliverable added");
  };

  const cycleDeliverableStatus = async (idx) => {
    const flow = ["not_started", "in_progress", "in_review", "delivered"];
    const list = [...(e.deliverables || [])];
    const cur = flow.indexOf(list[idx].status);
    const nextStatus = flow[(cur + 1) % flow.length];
    // Iter 85.65 · Stamp delivered_at whenever the transition lands on
    // `delivered`; clear it on any other status so the timestamp reflects
    // the most recent completion, not a stale prior one.
    list[idx] = {
      ...list[idx],
      status: nextStatus,
      delivered_at: nextStatus === "delivered" ? new Date().toISOString() : null,
    };
    await save({ deliverables: list });
    setSelectedDeliverable((prev) => (prev && prev.index === idx ? { item: list[idx], index: idx } : prev));
  };

  const openRoadmapPhase = (k) => {
    const p = (roadmap?.phases || []).find((x) => x.key === k);
    if (p) { setRoadmapPhase(p); setRoadmapDrawerOpen(true); }
  };

  return (
    <PageShell size="default">
      <EngagementHeader
        engagement={e}
        client={client}
        onAskAi={() => setAiOpen(true)}
        onDiagnostic={() => setDiagOpen(true)}
        onWeeklyBrief={() => setWeeklyOpen(true)}
        onCloseout={() => setCloseoutOpen(true)}
        onRename={(name) => save({ name })}
      />

      <EngagementStatusGrid
        engagement={e}
        onSave={save}
        onLocalPatch={(patch) => setE({ ...e, ...patch })}
      />

      {/*
        Overview lives ABOVE the playbook as its own section (Feb 2026) — it
        is the "what is this engagement" context that primes the deeper work
        below it. It was previously the first tab, which meant partners had
        to click into it to get their bearings.
      */}
      <EngagementOverviewTab engagement={e} engagementId={id} onSave={save} />

      {/* Iter 85.32 · Engagement-level decision log (mirrored from Briefing
          Studio accept). Hides itself when empty so it adds no visual
          weight to pre-briefing engagements. */}
      <EngagementDecisions engagement={e} />

      {(e.engagement_type === "executive_convening" || e.engagement_type === "trusted_advisor" || e.engagement_type === "strategic_initiative" || e.engagement_type === "executive_communication") && (
        <ConveningPlaybook engagement={e} onSaved={(fresh) => setE(fresh)} />
      )}

      <div ref={tabsRef} className="scroll-mt-4" aria-hidden />
      <Tabs defaultValue={initialTab} className="w-full" onValueChange={handleTabChange}>
        {/*
          Supporting tab row — theme-aware sticky bar (Feb 2026).
          Previously used a hardcoded `rgba(10,10,10,0.72)` background which
          worked in Dark but rendered as a heavy near-black slab on the Ivory
          Light board. Now uses `bg-card/85` + backdrop-blur so the row picks
          up Ivory in Light and Navy in Dark. Each trigger uses semantic
          tokens for inactive (`text-muted-foreground`), hover
          (`hover:text-foreground hover:bg-secondary/40`), and active
          (`text-primary` + Navy underline). Embrace is intentionally the
          first tab so the strategic lens leads the row.
        */}
        <TabsList
          className="rounded-none border-b border-border w-full justify-start p-0 h-auto overflow-x-auto sticky top-0 z-20 bg-card/85 backdrop-blur-md"
        >
          {/* Iter 85.62 · Strategic tab order per firm policy:
              Embrace · Activities · Deliverables · Tasks · Notes ·
              Documents · Milestones · Timeline · Timesheets · Invoices.
              Deep links via ?tab= continue to resolve — all trigger
              `value=` attributes are unchanged. */}
          <TabsTrigger value="roadmap" className={TAB_CLS} data-testid="tab-eng-embrace">Embrace</TabsTrigger>
          <TabsTrigger value="activities" className={TAB_CLS} data-testid="tab-eng-activities">Activities</TabsTrigger>
          <TabsTrigger value="deliverables" className={TAB_CLS} data-testid="tab-deliverables">Deliverables ({(e.deliverables || []).length})</TabsTrigger>
          <TabsTrigger value="tasks" className={TAB_CLS}>Tasks ({tasks.length})</TabsTrigger>
          <TabsTrigger value="notes" className={TAB_CLS} data-testid="tab-eng-notes">Notes ({notes.length})</TabsTrigger>
          <TabsTrigger value="documents" className={TAB_CLS} data-testid="tab-eng-documents">Documents</TabsTrigger>
          <TabsTrigger value="milestones" className={TAB_CLS}>Milestones ({(e.milestones || []).length})</TabsTrigger>
          <TabsTrigger value="timeline" className={TAB_CLS} data-testid="tab-eng-timeline">Timeline</TabsTrigger>
          <TabsTrigger value="timesheets" className={TAB_CLS} data-testid="tab-timesheets">Timesheets</TabsTrigger>
          <TabsTrigger value="invoices" className={TAB_CLS} data-testid="tab-eng-invoices">Invoices ({invoices.length})</TabsTrigger>
        </TabsList>

        {/* Iter 85.34 · Activities tab — dedicated view scoped to this
            engagement. Backed by the canonical notes collection filtered
            on note_type=activity_briefing + engagement_id. */}
        <TabsContent value="activities" className="mt-6">
          <ActivitiesTab scope="engagement" engagementId={id} clientId={e.client_id} engagements={[e]} />
        </TabsContent>

        <TabsContent value="roadmap" className="mt-6">
          <EngagementEmbraceTab
            engagementId={id}
            clientId={e.client_id}
            engagement={e}
            roadmap={roadmap}
            setRoadmap={setRoadmap}
            onOpenEmphasisDialog={() => setEmphasisDialogOpen(true)}
            canChangeEmphasis={canChangeEmphasis}
          />
        </TabsContent>

        <TabsContent value="milestones" className="mt-6">
          <EngagementMilestonesTab
            engagement={e}
            msOpen={msOpen}
            setMsOpen={setMsOpen}
            ms={ms}
            setMs={setMs}
            onAddMilestone={addMilestone}
            onToggleMilestone={toggleMilestone}
            onOpenMilestone={(item, index) => setSelectedMilestone({ item, index })}
          />
        </TabsContent>

        <TabsContent value="deliverables" className="mt-6">
          <EngagementDeliverablesTab
            engagement={e}
            dvOpen={dvOpen}
            setDvOpen={setDvOpen}
            dv={dv}
            setDv={setDv}
            onAddDeliverable={addDeliverable}
            onCycleStatus={cycleDeliverableStatus}
            onOpenDeliverable={(item, index) => setSelectedDeliverable({ item, index })}
            onEditDeliverable={editDeliverable}
            onRemoveDeliverable={deleteDeliverable}
            users={users}
          />
        </TabsContent>

        <TabsContent value="tasks" className="mt-6">
          <EngagementTasksTab
            engagement={e}
            tasks={tasks}
            onNewTask={() => setSelectedTask && setSelectedTask({})}
            engagements={[e]}
            activities={activities}
            users={users}
            onTaskChanged={() => api.get(`/tasks?engagement_id=${id}`).then((r) => setTasks(r.data))}
          />
        </TabsContent>

        <TabsContent value="timesheets" className="mt-6">
          <TimesheetsPanel engagementId={id} engagement={e} onEngagementUpdated={setE} />
        </TabsContent>

        <TabsContent value="invoices" className="mt-6" data-testid="engagement-invoices-panel">
          <EngagementInvoicesTab invoices={invoices} onOpenInvoice={setSelectedInvoice} />
        </TabsContent>

        <TabsContent value="documents" className="mt-6">
          <DocumentsPanel engagementId={id} />
        </TabsContent>

        <TabsContent value="notes" className="mt-6 space-y-3">
          <EngagementNotesTab notes={notes} onOpenNote={setSelectedNote} />
        </TabsContent>

        <TabsContent value="timeline" className="mt-6">
          <RelationshipTimeline engagementId={id} />
        </TabsContent>
      </Tabs>

      <YadidAI open={aiOpen} onClose={() => setAiOpen(false)} contextEngagementId={id} contextClientId={e.client_id} />
      <DiagnosticMemoDialog engagementId={id} open={diagOpen} onOpenChange={setDiagOpen} onSaved={() => load()} />
      <WeeklyBriefDialog engagementId={id} open={weeklyOpen} onOpenChange={setWeeklyOpen} onSaved={() => load()} />
      <CloseoutMemoDialog engagement={e} open={closeoutOpen} onOpenChange={setCloseoutOpen} onSaved={() => load()} />
      <NoteViewerDialog
        note={selectedNote}
        open={!!selectedNote}
        onOpenChange={(v) => { if (!v) { setSelectedNote(null); setFocusCommitmentIdx(null); } }}
        focusCommitmentIdx={focusCommitmentIdx}
      />
      <MilestoneViewerDialog
        milestone={selectedMilestone?.item}
        index={selectedMilestone?.index}
        open={!!selectedMilestone}
        onOpenChange={(v) => !v && setSelectedMilestone(null)}
        onToggle={toggleMilestone}
        onDelete={deleteMilestone}
      />
      <DeliverableViewerDialog
        deliverable={selectedDeliverable?.item}
        index={selectedDeliverable?.index}
        open={!!selectedDeliverable}
        onOpenChange={(v) => !v && setSelectedDeliverable(null)}
        onCycleStatus={cycleDeliverableStatus}
        onDelete={deleteDeliverable}
      />
      <TaskQuickViewDialog
        task={selectedTask}
        open={!!selectedTask}
        onOpenChange={(v) => !v && setSelectedTask(null)}
        onTaskChange={(updated) => {
          if (!updated) return;
          setTasks((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
          setSelectedTask((cur) => (cur && cur.id === updated.id ? { ...cur, ...updated } : cur));
        }}
      />
      <InvoiceQuickViewDialog invoice={selectedInvoice} open={!!selectedInvoice} onOpenChange={(v) => !v && setSelectedInvoice(null)} />
      <MethodPhaseDrawer
        open={roadmapDrawerOpen}
        onOpenChange={(v) => { setRoadmapDrawerOpen(v); if (!v) setRoadmapPhase(null); }}
        phase={roadmapPhase}
        mode="track"
        onSave={async (updated) => {
          const nextPhases = (roadmap?.phases || []).map((p) => (p.key === updated.key ? { ...p, ...updated } : p));
          const { data } = await api.put(`/engagements/${id}/roadmap`, { current_phase: roadmap.current_phase, phases: nextPhases });
          setRoadmap(data);
          setRoadmapDrawerOpen(false);
          setRoadmapPhase(null);
        }}
      />
      <MethodEmphasisDialog
        open={emphasisDialogOpen}
        onOpenChange={setEmphasisDialogOpen}
        engagementId={id}
        currentEmphasis={roadmap?.current_phase}
        onSaved={(fresh) => setRoadmap(fresh)}
      />
    </PageShell>
  );
}
